import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/siteData';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');
    setFormStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormSubmitting(false);
      return;
    }

    formData.delete('phone_alt');

    try {
      const urlSearchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        urlSearchParams.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9ose00senbniolesl0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlSearchParams.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的连接后重试');
      setFormStatus('error');
    } finally {
      setFormSubmitting(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: `${siteConfig.name}品牌介绍、口碑评价与联系方式。了解韩漫网的发展历程和核心价值观。`,
    url: `${siteConfig.url}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.name,
      description: aboutData.brandIntro,
      url: siteConfig.url,
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutData.contact.address,
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-4 pb-12">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="mt-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">关于韩漫网</h1>
          <p className="mt-4 text-foreground-600 leading-relaxed text-sm">{aboutData.brandIntro}</p>
        </section>

        <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {aboutData.stats.map((stat, i) => (
            <div key={i} className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
              <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">{stat.value}</div>
              <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </section>

        <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" id="mission">
          <div className="p-6 rounded-xl bg-background-100/70 border border-background-200/50">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-4">
              <i className="ri-focus-3-line text-primary-600 text-lg"></i>
            </div>
            <h2 className="text-lg font-bold text-foreground-900 font-heading">我们的使命</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
          </div>
          <div className="p-6 rounded-xl bg-background-100/70 border border-background-200/50">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
              <i className="ri-eye-line text-accent-600 text-lg"></i>
            </div>
            <h2 className="text-lg font-bold text-foreground-900 font-heading">我们的愿景</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
          </div>
        </section>

        <section className="mt-10" id="story">
          <h2 className="text-xl font-bold text-foreground-900 font-heading">品牌故事</h2>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{aboutData.story}</p>
        </section>

        <section className="mt-12" id="values">
          <h2 className="text-xl font-bold text-foreground-900 font-heading">核心价值观</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {aboutData.values.map((v, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className={`${v.icon} text-primary-600 text-lg`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900">{v.title}</h3>
                <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12" id="testimonials">
          <h2 className="text-xl font-bold text-foreground-900 font-heading">用户口碑</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {aboutData.testimonials.map((t, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-100/70 border border-background-200/50">
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-secondary-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12" id="contact">
          <h2 className="text-xl font-bold text-foreground-900 font-heading">联系我们</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                  <i className="ri-phone-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">电话</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">地址</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.address}</p>
                </div>
              </div>
            </div>

            <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-4 font-heading">给我们留言</h3>
              {formStatus === 'success' ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-check-line text-accent-600 text-xl"></i>
                  </div>
                  <p className="mt-3 text-sm font-medium text-foreground-900">提交成功！</p>
                  <p className="mt-1 text-xs text-foreground-500">感谢您的留言，我们会尽快回复</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言..."
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
                      ></textarea>
                    </div>
                    <div className="website-alt-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={formSubmitting}
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {formSubmitting ? '提交中...' : '发送留言'}
                    </button>
                  </div>
                  {formStatus === 'error' && formError && (
                    <p className="mt-3 text-xs text-red-500 text-center">{formError}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}